import type { ReactNode, CSSProperties, FormEvent } from "react";

export const COLORS = {
  bg: "#fafaf8",
  card: "#fff",
  text: "#1a1a1a",
  muted: "#666",
  border: "#e5e5e0",
  accent: "#3d2e1f",
  accentHover: "#1a1a1a",
  danger: "#a33",
  ok: "#2a7",
};

const baseFont = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ fontFamily: baseFont, color: COLORS.text }}>
      <h2 style={{ marginTop: 0, fontSize: 28, fontWeight: 600 }}>{title}</h2>
      {children}
    </div>
  );
}

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: 20,
        marginBottom: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Button({
  children,
  type = "button",
  onClick,
  variant = "primary",
  disabled,
  style,
}: {
  children: ReactNode;
  type?: "button" | "submit";
  onClick?: () => void;
  variant?: "primary" | "ghost" | "danger";
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const base: CSSProperties = {
    padding: "10px 18px",
    border: "none",
    borderRadius: 6,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600,
    fontSize: 14,
    fontFamily: "inherit",
    transition: "background 0.15s",
    opacity: disabled ? 0.5 : 1,
    ...style,
  };
  const variants: Record<string, CSSProperties> = {
    primary: { background: COLORS.accent, color: "#fff" },
    ghost: { background: "transparent", color: COLORS.text, border: `1px solid ${COLORS.border}` },
    danger: { background: "transparent", color: COLORS.danger, border: `1px solid ${COLORS.danger}` },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>
      {children}
    </button>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        padding: "10px 12px",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        fontSize: 14,
        fontFamily: "inherit",
        boxSizing: "border-box",
        ...(props.style ?? {}),
      }}
    />
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      style={{
        width: "100%",
        padding: "10px 12px",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        fontSize: 14,
        fontFamily: "inherit",
        boxSizing: "border-box",
        minHeight: 80,
        resize: "vertical",
        ...(props.style ?? {}),
      }}
    />
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.muted, marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

export function Form({
  onSubmit,
  children,
}: {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}) {
  return <form onSubmit={onSubmit}>{children}</form>;
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: 32, textAlign: "center", color: COLORS.muted, fontStyle: "italic" }}>
      {children}
    </div>
  );
}

export function Badge({ children, kind = "neutral" }: { children: ReactNode; kind?: "neutral" | "ok" | "warn" }) {
  const colors: Record<string, { bg: string; text: string }> = {
    neutral: { bg: "#eee", text: "#444" },
    ok: { bg: "#e7f5ee", text: "#1c6b3a" },
    warn: { bg: "#fef3c7", text: "#92400e" },
  };
  const c = colors[kind];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        background: c.bg,
        color: c.text,
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}
